import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage, useIonAlert } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Crosshair, MessageCircle, Navigation, Phone, Satellite, TriangleAlert, UserX } from 'lucide-react';
import { DRIVER_ACTION_FOR, RIDE_STATUS_LABEL } from '@tcrd/shared';
import { Avatar, Badge, Button, Card, Notice, Spinner } from '@tcrd/ui';
import { MapView } from '../../components/map/MapView';
import type { MapEngine, MarkerSpec } from '../../components/map/engine';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDuration, fmtKm } from '../../lib/format';
import { currentPosition } from '../../lib/location';
import { rideTracker, type TrackerStatus } from '../../lib/tracking';
import { useRide } from '../common/RideDetailPage';
import { useLiveRide } from '../common/useLiveRide';
import { SafetyCenter } from '../safety/SafetyCenter';
import { DestinationChangeDriver } from '../safety/DestinationChange';
import { TripNeeds } from '../../components/TripExtras';

const TRACKING = ['DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'];

function useTrackerStatus() {
  const [s, setS] = useState<TrackerStatus | null>(null);
  useEffect(() => {
    const off = rideTracker.subscribe(setS);
    return () => void off();
  }, []);
  return s;
}

/** Viaje activo del conductor: mapa centrado en el vehículo y acciones del servicio. */
export function ActiveRidePage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const ride = useRide(id);
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const [follow, setFollow] = useState(true);
  const live = useLiveRide(id, engine, { follow });
  const gps = useTrackerStatus();
  const [busy, setBusy] = useState(false);
  const r = ride.data;
  const nav = live.snapshot.data?.navigation;

  // El GPS solo corre durante los estados autorizados.
  useEffect(() => {
    if (!r) return;
    if (TRACKING.includes(r.status)) void rideTracker.start(r.id, live.snapshot.data?.gpsIntervalSeconds ?? 4);
    else if (rideTracker.activeRideId === r.id) void rideTracker.stop();
  }, [r, live.snapshot.data?.gpsIntervalSeconds]);

  useEffect(() => {
    engine?.onUserGesture(() => setFollow(false));
  }, [engine]);

  // Paradas que faltan (en orden) durante el viaje.
  const pendingStops = useMemo(() => (r ? r.stops.slice(r.stopsVisited) : []), [r]);
  const markers = useMemo<MarkerSpec[]>(() => {
    if (!r) return [];
    const inTrip = r.status === 'IN_PROGRESS';
    const stops = (inTrip ? pendingStops : r.stops).map((s, i) => ({ id: `s${i}`, position: s, kind: 'stop' as const, label: String(i + 1 + (inTrip ? r.stopsVisited : 0)) }));
    const dest: MarkerSpec = { id: 'd', position: r.destination, kind: 'destination' };
    return inTrip ? [...stops, dest] : [{ id: 'o', position: r.origin, kind: 'origin' }, ...stops, dest];
  }, [r, pendingStops]);

  const target = r && (r.status === 'IN_PROGRESS' ? r.destination : r.origin);
  const openExternal = (app: 'google' | 'waze') => {
    if (!target || !r) return;
    const via = r.status === 'IN_PROGRESS' ? pendingStops : [];
    // Google Maps admite paradas intermedias; Waze no: se navega a la próxima parada.
    const wazeTo = via[0] ?? target;
    const waypoints = via.length ? `&waypoints=${encodeURIComponent(via.map((s) => `${s.lat},${s.lng}`).join('|'))}` : '';
    const url =
      app === 'google'
        ? `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}${waypoints}&travelmode=driving`
        : `https://waze.com/ul?ll=${wazeTo.lat},${wazeTo.lng}&navigate=yes`;
    // Abre la app de navegación; el seguimiento de TuConductorRD sigue activo en segundo plano.
    window.open(url, '_blank');
  };

  const doAction = async (action: string, pin?: string) => {
    setBusy(true);
    try {
      let pos: { lat: number; lng: number } | undefined;
      if (action === 'start-pickup' || action === 'start' || action === 'complete') {
        pos = (await currentPosition().catch(() => null)) ?? live.currentPosition() ?? undefined;
      }
      const body = { ...(pos ? { lat: pos.lat, lng: pos.lng } : {}), ...(pin ? { pin } : {}) };
      const res = await api<{ endLocationWarning?: boolean; distanceToDestination?: number }>(`/rides/${id}/${action}`, { body });
      await Promise.all([qc.invalidateQueries({ queryKey: ['ride', id] }), live.snapshot.refetch(), qc.invalidateQueries({ queryKey: ['rides'] })]);
      if (action === 'complete') {
        await rideTracker.stop();
        if (res.endLocationWarning) toast(`Viaje finalizado a ${fmtKm(res.distanceToDestination ?? 0)} del destino registrado`, 'dark');
        history.replace(`/driver/rides/${id}/payment`);
      }
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const action = r ? DRIVER_ACTION_FOR[r.status] : undefined;
  const onAction = () => {
    if (!action) return;
    if (action.action === 'complete') {
      alert({
        header: '¿Finalizar viaje?',
        message: 'Se detendrá el GPS y podrás registrar el cobro. El pago no es necesario para cerrar el recorrido.',
        buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Finalizar', handler: () => void doAction('complete') }],
      });
    } else if (action.action === 'start' && r?.pin?.required && !r.pin.verified) {
      // PIN de abordaje: el cliente lo dice al subir.
      alert({
        header: 'PIN del cliente',
        message: 'Pídele al cliente su código de 4 dígitos.',
        inputs: [{ name: 'pin', type: 'number', placeholder: '0000', attributes: { inputmode: 'numeric', maxlength: 4 } }],
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Iniciar viaje', handler: (v: { pin: string }) => void doAction('start', String(v.pin ?? '').trim().padStart(4, '0')) },
        ],
      });
    } else void doAction(action.action);
  };

  // "El cliente no se presentó": disponible tras la espera configurada en el punto de recogida.
  const [, tick] = useState(0);
  useEffect(() => {
    if (r?.status !== 'DRIVER_ARRIVED') return;
    const t = setInterval(() => tick((x) => x + 1), 15_000);
    return () => clearInterval(t);
  }, [r?.status]);
  const waitMin = live.snapshot.data?.noShowWaitMinutes ?? 5;
  const noShowAt = r?.timestamps.arrivedAt ? new Date(r.timestamps.arrivedAt).getTime() + waitMin * 60_000 : null;
  const noShowLeft = noShowAt ? Math.max(0, Math.ceil((noShowAt - Date.now()) / 60_000)) : null;
  const confirmNoShow = async () => {
    try {
      await api(`/rides/${id}/no-show`, { method: 'POST' });
      await rideTracker.stop();
      await Promise.all([qc.invalidateQueries({ queryKey: ['ride', id] }), qc.invalidateQueries({ queryKey: ['rides'] })]);
      toast('Viaje cancelado: el cliente no se presentó', 'dark');
      history.replace(`/driver/rides/${id}`);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };
  const noShow = () =>
    alert({
      header: '¿El cliente no se presentó?',
      message: 'El viaje se cancelará y se aplicará el cargo por cancelación del negocio (si existe).',
      buttons: [
        { text: 'Seguir esperando', role: 'cancel' },
        { text: 'Cancelar viaje', role: 'destructive', handler: () => void confirmNoShow() },
      ],
    });

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="relative h-full">
          <MapView onEngine={setEngine} className="absolute inset-0" markers={markers} route={nav?.polyline ?? r?.route.polyline ?? null} fitKey={`${id}-${nav?.target ?? ''}`} padding={80} />
          <Link to={`/driver/rides/${id}`} className="absolute left-4 top-[max(env(safe-area-inset-top),1rem)] tap size-11 rounded-full bg-[var(--surface)] shadow-[var(--shadow-float)] flex items-center justify-center" aria-label="Volver">
            <ArrowLeft className="size-5" />
          </Link>
          <div className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] flex flex-col items-end gap-2">
            {r && <SafetyCenter ride={r} role="driver" />}
            {gps?.running && (
              <span className={`rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${gps.error ? 'bg-brand-red text-white' : 'bg-black/75 text-white'}`}>
                <Satellite className="size-4" aria-hidden />
                {gps.error ? 'GPS con error' : gps.lastAccuracy ? `GPS ±${Math.round(gps.lastAccuracy)} m` : 'Buscando GPS…'}
                {gps.queued > 1 ? ` · ${gps.queued} sin enviar` : ''}
              </span>
            )}
            {!follow && (
              <button className="tap rounded-full bg-[var(--surface)] shadow-[var(--shadow-float)] px-3 text-sm font-medium flex items-center gap-1.5" onClick={() => setFollow(true)}>
                <Crosshair className="size-4" aria-hidden /> Centrar
              </button>
            )}
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            {!r ? (
              <Card className="p-6"><Spinner /></Card>
            ) : (
              <Card className="p-5 shadow-[var(--shadow-float)] max-w-xl mx-auto flex flex-col gap-4 max-h-[75vh] overflow-y-auto">
                <DestinationChangeDriver ride={r} onDone={() => void live.snapshot.refetch()} />
                {r.status === 'IN_PROGRESS' && pendingStops.length > 0 && (
                  <p className="text-sm rounded-2xl bg-[var(--surface-2)] px-4 py-2">
                    Próxima parada: <strong>{pendingStops[0]!.address}</strong>
                    {pendingStops.length > 1 ? ` (+${pendingStops.length - 1})` : ''}
                  </p>
                )}
                <TripNeeds needs={r.needs} flightNumber={r.flightNumber} pkg={r.package} />
                {r.package && r.status === 'IN_PROGRESS' && !r.package.deliveryPhotoUrl && (
                  <Link to={`/driver/rides/${r.id}`}><Button block variant="secondary">Registrar la entrega (foto)</Button></Link>
                )}
                {r.pin?.required && !r.pin.verified && r.status !== 'IN_PROGRESS' && (
                  <p className="text-xs text-[var(--text-muted)]">Este cliente usa PIN: pídeselo al subir para iniciar el viaje.</p>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Badge tone="dark">{RIDE_STATUS_LABEL[r.status]}</Badge>
                    {live.snapshot.data?.live && live.eta !== null && (
                      <p className="mt-1 text-lg font-bold">{fmtDuration(live.eta)} <span className="text-sm font-normal text-[var(--text-muted)]">· {fmtKm(live.remaining)}</span></p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" icon={<Navigation className="size-4" />} onClick={() => openExternal('google')}>Maps</Button>
                    <Button size="sm" variant="secondary" onClick={() => openExternal('waze')}>Waze</Button>
                  </div>
                </div>
                {gps?.error && (
                  <Notice tone="danger">
                    {gps.error} {gps.permission === 'denied' && <button className="underline font-semibold" onClick={() => void rideTracker.openSettings()}>Abrir ajustes</button>}
                  </Notice>
                )}
                {r.customer && (
                  <div className="flex items-center gap-3">
                    <Avatar src={r.customer.photoUrl} firstName={r.customer.firstName} lastName={r.customer.lastName} size={48} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{r.customer.contactName ?? `${r.customer.firstName} ${r.customer.lastName}`}</p>
                      <p className="text-sm text-[var(--text-muted)] truncate">{r.status === 'IN_PROGRESS' ? r.destination.address : r.origin.address}</p>
                    </div>
                    {r.conversationId && (
                      <Link to={`/driver/chat/${r.conversationId}`} className="tap size-11 rounded-full bg-[var(--surface-2)] flex items-center justify-center" aria-label="Chat"><MessageCircle className="size-5" /></Link>
                    )}
                    {r.customer.phone && (
                      <a href={`tel:${r.customer.phone}`} className="tap size-11 rounded-full bg-[var(--surface-2)] flex items-center justify-center" aria-label="Llamar"><Phone className="size-5" /></a>
                    )}
                  </div>
                )}
                {r.status === 'COMPLETED' && (
                  <Link to={`/driver/rides/${id}/payment`}><Button block variant="accent">Registrar cobro</Button></Link>
                )}
                {action && (
                  <Button block size="lg" variant={action.action === 'complete' ? 'accent' : 'primary'} loading={busy} onClick={onAction}>
                    {action.label}
                  </Button>
                )}
                {r.status === 'DRIVER_ARRIVED' && noShowLeft !== null && (
                  <Button variant="ghost" block icon={<UserX className="size-5" />} disabled={noShowLeft > 0} onClick={noShow}>
                    {noShowLeft > 0 ? `El cliente no se presentó (en ${noShowLeft} min)` : 'El cliente no se presentó'}
                  </Button>
                )}
                {r.status === 'CONFIRMED' && (
                  <p className="text-xs text-[var(--text-muted)] flex items-start gap-1.5"><TriangleAlert className="size-4 shrink-0" aria-hidden /> Al pulsar "Ir a recoger" se compartirá tu ubicación con el cliente únicamente hasta finalizar el viaje.</p>
                )}
              </Card>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
